import type { QueryClient } from "@tanstack/react-query";

/**
 * Keeps every open tab of the app in step with the others.
 *
 * Each tab has its own query cache, and a mutation only invalidates the cache
 * of the tab that made it — so a second tab kept showing an employee list, a
 * request inbox or a company that had since changed, until a staleTime ran out
 * or the page was reloaded. Tabs now tell each other over a `BroadcastChannel`:
 *
 *  - **write**: some request changed data. Every other tab marks its whole
 *    cache stale. A visible tab refetches what is on screen; a hidden one
 *    refetches when it is next focused (React Query's `refetchOnWindowFocus`),
 *    so a background tab costs nothing until someone looks at it.
 *  - **account**: the signed-in account or its active company changed (sign-out,
 *    session expiry, a company switch, an accepted invite). The cookie is shared
 *    across tabs, so every other tab is now talking to the server as someone
 *    else; its cache is dropped outright rather than refetched in place, and
 *    `onAccountChange` re-runs the route guards.
 *
 * Nothing is shared *through* the channel but those two signals — each tab
 * still fetches its own data from the API, which stays the only source of
 * truth.
 */

type Message = { type: "write" } | { type: "account" };

const CHANNEL_NAME = "hr-docket:query-cache";

/** Writes arrive in bursts (a save plus its follow-ups); tell the others once. */
const WRITE_COALESCE_MS = 250;

/**
 * How long after hearing of an account change this tab stays quiet about its
 * own. Dropping the cache can make this tab's next requests fail with a 401,
 * and the session-expiry handler answers that by clearing the cache — which
 * would announce the same change straight back.
 */
const ACCOUNT_ECHO_MS = 5_000;

let channel: BroadcastChannel | null = null;
let writeTimer: ReturnType<typeof setTimeout> | undefined;
let heardAccountChangeAt = 0;

function post(message: Message): void {
  try {
    channel?.postMessage(message);
  } catch {
    // A closed channel (the tab is unloading) has no one left to tell.
  }
}

/** A request from this tab changed data on the server. */
export function announceWrite(): void {
  if (!channel) return;
  clearTimeout(writeTimer);
  writeTimer = setTimeout(() => post({ type: "write" }), WRITE_COALESCE_MS);
}

/** This tab signed out, or moved the session to another account or company. */
export function announceAccountChange(): void {
  if (Date.now() - heardAccountChangeAt < ACCOUNT_ECHO_MS) return;
  post({ type: "account" });
}

/**
 * Starts listening. Call once, from the app entry. A browser without
 * `BroadcastChannel` simply keeps the old per-tab behaviour.
 */
export function installTabSync(
  queryClient: QueryClient,
  { onAccountChange }: { onAccountChange: () => void },
): void {
  if (channel || typeof BroadcastChannel === "undefined") return;
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent<Message>) => {
    switch (event.data?.type) {
      case "write":
        void queryClient.invalidateQueries({
          refetchType: document.visibilityState === "visible" ? "active" : "none",
        });
        break;
      case "account":
        heardAccountChangeAt = Date.now();
        queryClient.clear();
        onAccountChange();
        break;
    }
  };
}
